"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  Skeleton,
  FormControl,
  Tab,
  MenuItem,
  Tabs,
  Alert,
  InputLabel,
  Select,
  Button,
  Tooltip,TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material"
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts"
import {
  Schedule,
  Person,
  Work,
  Assignment,
  PersonAdd,
  Groups,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  TrendingUp,
  Psychology,
  PictureAsPdf,
  Description,
  FilterAlt,
  Close
} from "@mui/icons-material"
import html2pdf from 'html2pdf.js';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const RecruitmentReports = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [exportLoading, setExportLoading] = useState({ pdf: false, doc: false });

  const [allOffers, setAllOffers] = useState([]);
  // Data states
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [totalInterviews, setTotalInterviews] = useState(0);
  const [onboardings, setOnboardings] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Dialog state for drill-down details
  const [dialogState, setDialogState] = useState({
    open: false,
    title: '',
    type: '',
    data: []
  });
  const [dialogPage, setDialogPage] = useState(0);
  const [dialogRowsPerPage, setDialogRowsPerPage] = useState(5);

  const openModal = (type, data, title) => {
    setDialogState({
      open: true,
      title,
      type,
      data: data || []
    });
    setDialogPage(0);
  };

  const closeModal = () => {
    setDialogState(prev => ({ ...prev, open: false }));
  };

  // Robust helpers to resolve Candidate Name and Email across all data structures
  const resolveCandidateName = (item) => {
    if (!item) return 'N/A';
    // 1. Direct name properties
    let name = item.candidateName || 
               item.candidate_name || 
               item.name || 
               item.candidate?.name || 
               item.Candidate?.name || 
               item.candidateDetails?.name || 
               item.Interview?.name || 
               item.Interview?.candidateName;

    if (name && name !== 'Candidate Information Pending' && String(name).trim() !== '') return name;

    // 2. Lookup in candidates list state by email or candidateId
    const email = item.candidateEmail || 
                  item.candidate_email || 
                  item.email || 
                  item.candidate?.email || 
                  item.Candidate?.email || 
                  item.Interview?.candidateEmail;
                  
    const candId = item.candidateId || item.candidate_id || item.id;

    if (email || candId) {
      const match = candidates.find(c => 
        (email && c.email && c.email.toLowerCase() === email.toLowerCase()) ||
        (candId && c.id && String(c.id) === String(candId))
      );
      if (match && match.name && String(match.name).trim() !== '') return match.name;
    }

    // 3. Lookup in interviews list if available
    if (email) {
      const matchInterview = interviews.find(i => 
        (i.candidateEmail && i.candidateEmail.toLowerCase() === email.toLowerCase()) ||
        (i.email && i.email.toLowerCase() === email.toLowerCase())
      );
      if (matchInterview && (matchInterview.name || matchInterview.candidateName)) {
        return matchInterview.name || matchInterview.candidateName;
      }
    }

    // 4. Lookup in onboardings list if available
    if (email) {
      const matchOnboarding = onboardings.find(o => 
        (o.candidateEmail && o.candidateEmail.toLowerCase() === email.toLowerCase())
      );
      if (matchOnboarding && matchOnboarding.candidateName) {
        return matchOnboarding.candidateName;
      }
    }

    // 5. Fallback formatting from email username if email exists (e.g. snehapoojary@gmail.com -> Sneha Poojary)
    if (email && email.includes('@')) {
      const uname = email.split('@')[0];
      const cleaned = uname.replace(/[._-]/g, ' ');
      return cleaned.replace(/\b\w/g, l => l.toUpperCase());
    }

    if (name === 'Candidate Information Pending') return 'Information Pending';

    return 'N/A';
  };

  const resolveCandidateEmail = (item) => {
    if (!item) return 'N/A';
    return item.candidateEmail || 
           item.candidate_email || 
           item.email || 
           item.candidate?.email || 
           item.Candidate?.email || 
           item.candidateDetails?.email || 
           item.Interview?.candidateEmail || 
           'N/A';
  };

  const DetailDialog = () => {
    const { open, title, type, data } = dialogState;
    const paginatedDialogData = data.slice(
      dialogPage * dialogRowsPerPage,
      dialogPage * dialogRowsPerPage + dialogRowsPerPage
    );

    return (
      <Dialog open={open} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight="bold">{title}</Typography>
            <Chip label={`${data.length} Records`} color="primary" size="small" />
          </Box>
          <IconButton onClick={closeModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {data.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No records found for this selection.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  {type === 'candidates' && (
                    <>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Candidate Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Position Applied</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Experience</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Application Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDialogData.map((candidate, idx) => (
                          <TableRow key={candidate.id || idx} hover>
                            <TableCell>{resolveCandidateName(candidate)}</TableCell>
                            <TableCell>{resolveCandidateEmail(candidate)}</TableCell>
                            <TableCell>{candidate.phone || 'N/A'}</TableCell>
                            <TableCell>{candidate.positionApplied || 'N/A'}</TableCell>
                            <TableCell>{candidate.experience ? `${candidate.experience} yrs` : 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={candidate.referrerName ? 'Referred' : 'Direct'}
                                color={candidate.referrerName ? 'success' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {type === 'jobs' && (
                    <>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Job ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Job Title</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Employment Type</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Closing Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDialogData.map((job, idx) => (
                          <TableRow key={job.id || job.jobId || idx} hover>
                            <TableCell>{job.jobId || job.id || 'N/A'}</TableCell>
                            <TableCell>{job.jobTitle || job.job_title || job.title || 'N/A'}</TableCell>
                            <TableCell>{job.department || 'N/A'}</TableCell>
                            <TableCell>{job.location || 'N/A'}</TableCell>
                            <TableCell>{job.employmentType || job.employment_type || 'Full-time'}</TableCell>
                            <TableCell>
                              <Chip
                                label={job.status || 'Open'}
                                color={getStatusColor(job.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{job.jobClosedDate ? new Date(job.jobClosedDate).toLocaleDateString() : 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {type === 'interviews' && (
                    <>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Candidate Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Interview Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Round</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Interviewer</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDialogData.map((interview, idx) => (
                          <TableRow key={interview.id || idx} hover>
                            <TableCell>{resolveCandidateName(interview)}</TableCell>
                            <TableCell>{interview.positionApplied || 'N/A'}</TableCell>
                            <TableCell>{interview.interviewDate ? new Date(interview.interviewDate).toLocaleDateString() : 'N/A'}</TableCell>
                            <TableCell><Chip label={interview.round || 'N/A'} size="small" variant="outlined" /></TableCell>
                            <TableCell>{interview.interviewer || 'N/A'}</TableCell>
                            <TableCell><Chip label={interview.status || 'Scheduled'} color={getStatusColor(interview.status)} size="small" /></TableCell>
                            <TableCell>{interview.result ? <Chip label={interview.result} color={getStatusColor(interview.result)} size="small" /> : 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {type === 'offers' && (
                    <>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Candidate Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Job Title</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Fixed Salary</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Offer Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDialogData.map((offer, idx) => (
                          <TableRow key={offer.id || idx} hover>
                            <TableCell>{resolveCandidateName(offer)}</TableCell>
                            <TableCell>{resolveCandidateEmail(offer)}</TableCell>
                            <TableCell>{offer.jobTitle || offer.job_title || 'N/A'}</TableCell>
                            <TableCell>{offer.salaryFixed || offer.fixedSalary || 'N/A'}</TableCell>
                            <TableCell>{offer.offerDate || offer.offer_date ? new Date(offer.offerDate || offer.offer_date).toLocaleDateString() : 'N/A'}</TableCell>
                            <TableCell><Chip label={offer.status || 'Pending'} color={getStatusColor(offer.status)} size="small" /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {type === 'onboardings' && (
                    <>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Candidate Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Employee ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Process</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Completion Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDialogData.map((onboarding, idx) => (
                          <TableRow key={onboarding.id || idx} hover>
                            <TableCell>{resolveCandidateName(onboarding)}</TableCell>
                            <TableCell>{onboarding.employee_id || 'N/A'}</TableCell>
                            <TableCell>{resolveCandidateEmail(onboarding)}</TableCell>
                            <TableCell>{onboarding.onboardingProcess?.replace('-', ' ') || 'N/A'}</TableCell>
                            <TableCell><Chip label={onboarding.status || 'Pending'} color={getStatusColor(onboarding.status)} size="small" /></TableCell>
                            <TableCell>{onboarding.taskCompletionDate ? new Date(onboarding.taskCompletionDate).toLocaleDateString() : 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={data.length}
                page={dialogPage}
                onPageChange={(e, newPage) => setDialogPage(newPage)}
                rowsPerPage={dialogRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setDialogRowsPerPage(parseInt(e.target.value, 10));
                  setDialogPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  // Updated API base URL to match your backend

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch available years
  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${API_BASE}/api/recruitment-report/years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.years && data.years.length > 0) {
          setAvailableYears(data.years);
          if (!data.years.includes(selectedYear)) {
            setSelectedYear(data.years[0]);
          }
        }
      }
    } catch (e) {
      //console.error('Error fetching available years:', e);
    }
  };

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  // Fetch data function with proper error handling
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeaders();
      const yearParam = dateRange === 'yearly' ? `&year=${selectedYear}` : '';
      const response = await fetch(`${API_BASE}/api/recruitment-report/summary?period=${dateRange}${yearParam}`, { headers });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch report summary: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.status === "success" && result.data) {
        const {
          candidates = [],
          processedJobs = [],
          interviews = [],
          allOffers = [],
          formattedOnboardings = []
        } = result.data;
        
        setCandidates(candidates);
        setJobs(processedJobs);
        setInterviews(interviews);
        setTotalInterviews(interviews.length);
        setAllOffers(allOffers);
        setOnboardings(formattedOnboardings);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      setError('Failed to fetch data. Please check your connection and try again.');
      //console.error('Error fetching data:', err);
      setCandidates([]);
      setJobs([]);
      setInterviews([]);
      setTotalInterviews(0);
      setAllOffers([]);
      setOnboardings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedYear]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
  };

  // Calculate various statistics
  const getJobStats = () => {
    const activeJobs = jobs.filter(job => job.status === "Open").length;
    const closingSoonJobs = jobs.filter(job => job.status === "Closing Soon").length;
    const closedJobs = jobs.filter(job => job.status === "Closed").length;
    
    return { activeJobs, closingSoonJobs, closedJobs };
  };

  const getInterviewStats = () => {
    const scheduled = interviews.filter(interview => 
      interview.status?.toLowerCase() === 'scheduled' ||
      interview.status?.toLowerCase() === 'pending'
    ).length;
    
    const completed = interviews.filter(interview => 
      interview.status?.toLowerCase() === 'completed'
    ).length;
    
    const selected = interviews.filter(interview => 
      interview.result?.toLowerCase() === 'selected' ||
      interview.status?.toLowerCase() === 'selected'
    ).length;
    
    const rejected = interviews.filter(interview => 
      interview.result?.toLowerCase() === 'rejected' ||
      interview.status?.toLowerCase() === 'rejected'
    ).length;
    
    const cancelled = interviews.filter(interview => 
      interview.status?.toLowerCase() === 'cancelled'
    ).length;
    
    return { scheduled, completed, selected, rejected, cancelled };
  };

  const getOfferStats = () => {
    return {
      totalOffers: allOffers.length,
      acceptedOffers: allOffers.filter(offer => offer.status?.toLowerCase() === 'accepted').length,
      pendingOffers: allOffers.filter(offer => offer.status?.toLowerCase() === 'pending').length
    };
  };
  
  const getOnboardingStats = () => {
    
    const pending = onboardings.filter(onboarding => 
      onboarding.status?.toLowerCase() === 'pending'
    ).length;
    
    const inProgress = onboardings.filter(onboarding => 
      onboarding.status?.toLowerCase() === 'in-progress' ||
      onboarding.status?.toLowerCase() === 'in progress'
    ).length;
    
    const completed = onboardings.filter(onboarding => 
      onboarding.status?.toLowerCase() === 'completed'
    ).length;
    
    
    return { pending, inProgress, completed, total: onboardings.length };
  };

  // FIXED: Prediction functions - hiring prediction now uses onboarding data
  const generateReferralPrediction = () => {
    const currentReferred = candidates.filter(c => c.referrerName).length;
    const total = candidates.length;
    const referralRate = total > 0 ? (currentReferred / total) * 100 : 0;
    
    // Simple prediction based on current trend
    const nextMonthPrediction = Math.round(currentReferred * 1.15);
    const nextQuarterPrediction = Math.round(currentReferred * 1.45);
    const nextYearPrediction = Math.round(currentReferred * 2.2);
    
    return {
      current: currentReferred,
      rate: referralRate,
      predictions: {
        month: nextMonthPrediction,
        quarter: nextQuarterPrediction,
        year: nextYearPrediction
      }
    };
  };

  // FIXED: Hiring prediction now uses onboarding data
  const generateHiringPrediction = () => {
    // Get hired count from completed onboarding processes
    const currentHired = onboardings.filter(o => o.status?.toLowerCase() === 'completed').length;
    const totalOnboarding = onboardings.length;
    const totalCandidates = candidates.length;
    
    // Calculate hiring rate based on onboarding completion
    const hiringRate = totalOnboarding > 0 ? (currentHired / totalOnboarding) * 100 : 0;
    const overallHiringRate = totalCandidates > 0 ? (currentHired / totalCandidates) * 100 : 0;
    
    // Prediction based on current onboarding pipeline
    const inProgressOnboarding = onboardings.filter(o => 
      o.status?.toLowerCase() === 'in-progress' || 
      o.status?.toLowerCase() === 'in progress'
    ).length;
    
    const pendingOnboarding = onboardings.filter(o => 
      o.status?.toLowerCase() === 'pending'
    ).length;
    
    // More realistic predictions based on onboarding data
    const nextMonthPrediction = Math.round(currentHired + (inProgressOnboarding * 0.7) + (pendingOnboarding * 0.3));
    const nextQuarterPrediction = Math.round(currentHired + inProgressOnboarding + (pendingOnboarding * 0.8));
    const nextYearPrediction = Math.round(currentHired * 2.5 + totalOnboarding);
    
 
    
    return {
      current: currentHired,
      rate: hiringRate,
      overallRate: overallHiringRate,
      totalOnboarding,
      inProgressOnboarding,
      pendingOnboarding,
      predictions: {
        month: nextMonthPrediction,
        quarter: nextQuarterPrediction,
        year: nextYearPrediction
      }
    };
  };

  // Get status color helper
  const getStatusColor = (status) => {
    const statusColors = {
      'pending': 'warning',
      'scheduled': 'info',
      'completed': 'success',
      'cancelled': 'error',
      'approved': 'success',
      'rejected': 'error',
      'selected': 'success',
      'in-progress': 'primary',
      'in progress': 'primary',
      'active': 'success',
      'inactive': 'default',
      'open': 'success',
      'closing soon': 'warning',
      'closed': 'error'
    };
    return statusColors[status?.toLowerCase()] || 'default';
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Box>
      {[...Array(5)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
      ))}
    </Box>
  );

  // Function to generate HTML content for export (tables only, no graphs)
  const generateExportHTML = (options = {}) => {
    const { includeSummary = true } = options;
    const currentDate = new Date().toLocaleDateString();
    const periodText = dateRange === 'monthly' ? 'Monthly' : dateRange === 'quarterly' ? 'Quarterly' : `Yearly (${selectedYear})`;
    
    // Helper function to get job status for export
    const getJobStatusForExport = (job) => {
      if (!job.jobClosedDate) return job.status || 'Open';
      const closingDate = new Date(job.jobClosedDate);
      const today = new Date();
      const daysDifference = Math.ceil((closingDate - today) / (1000 * 60 * 60 * 24));
      
      if (closingDate < today) return 'Closed';
      if (daysDifference <= 7) return 'Closing Soon';
      return 'Open';
    };
    
    // Helper function for interview status
    const getInterviewStatusForExport = (interview) => {
      return interview.status || 'Scheduled';
    };
    
    // Helper function for onboarding status
    const calculateOnboardingStatusForExport = (request) => {
      const requiredDocs = request.requiredDocuments || [];
      const uploadedDocs = request.uploadedDocuments || [];
      
      if (requiredDocs.length === 0) return 'No Requirements';
      if (uploadedDocs.length === 0) return 'Pending';
      
      const uploadedDocTypes = uploadedDocs.map(doc => doc.documentType).filter(Boolean);
      const missingDocs = requiredDocs.filter(reqDoc => !uploadedDocTypes.includes(reqDoc));
      
      if (missingDocs.length === 0) {
        const allApproved = uploadedDocs.every(doc => doc.status === 'Approved');
        const hasRejected = uploadedDocs.some(doc => doc.status === 'Rejected');
        
        if (hasRejected) return 'Action Required';
        if (allApproved) return 'Completed';
        return 'Under Review';
      }
      return 'In Progress';
    };
    
    // Generate HTML content
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Recruitment Report - ${periodText} Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333333;
          }
          h1 {
            color: #222222;
            border-bottom: 2px solid #222222;
            padding-bottom: 10px;
          }
          h2 {
            color: #222222;
            margin-top: 25px;
            margin-bottom: 12px;
            background-color: #f8f9fa;
            padding: 6px;
            border-left: 4px solid #555555;
            font-size: 18px;
          }
          .report-header {
            text-align: center;
            margin-bottom: 25px;
          }
          .report-date {
            color: #666666;
            font-size: 12px;
            margin-top: 5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
            font-size: 11px;
            page-break-inside: avoid;
          }
          th {
            background-color: #4a4a4a;
            color: white;
            padding: 8px;
            text-align: left;
            border: 1px solid #cccccc;
          }
          td {
            padding: 6px;
            border: 1px solid #dddddd;
            text-align: left;
          }
          tr:nth-child(even) {
            background-color: #fafafa;
          }
          .summary-card {
            background-color: #f8f9fa;
            padding: 12px;
            margin-bottom: 20px;
            border: 1px solid #e9ecef;
            border-radius: 5px;
            page-break-inside: avoid;
          }
          .summary-stats {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            flex-wrap: wrap;
          }
          .stat-item {
            text-align: center;
            flex: 1;
            min-width: 100px;
            padding: 8px;
          }
          .stat-value {
            font-size: 22px;
            font-weight: bold;
            color: #222222;
          }
          .stat-label {
            font-size: 11px;
            color: #666666;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #999999;
            border-top: 1px solid #dddddd;
            padding-top: 15px;
          }
          @media print {
            body {
              margin: 0;
              padding: 10px;
            }
            .page-break {
              page-break-before: always;
            }
            table {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <h1>Recruitment Report - ${periodText} Report</h1>
          <div class="report-date">Generated on: ${currentDate}</div>
        </div>
        
        ${includeSummary ? `
        <!-- Summary Section -->
        <div class="summary-card">
          <h2>Summary Statistics</h2>
          <div class="summary-stats">
            <div class="stat-item">
              <div class="stat-value">${candidates.length}</div>
              <div class="stat-label">Total Applications</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${getJobStats().activeJobs}</div>
              <div class="stat-label">Active Jobs</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${totalInterviews}</div>
              <div class="stat-label">Interviews Scheduled</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${allOffers.length}</div>
              <div class="stat-label">Offers Sent</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${onboardings.length}</div>
              <div class="stat-label">Onboarding Process</div>
            </div>
          </div>
        </div>
        ` : ''}
        
        <!-- Applications Table -->
        <h2>Applications Report</h2>
        ${candidates.length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>Candidate Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Position Applied</th>
              <th>Skills</th>
              <th>Experience</th>
              <th>Application Type</th>
            </tr>
          </thead>
          <tbody>
            ${candidates.map(candidate => `
              <tr>
                <td>${candidate.name || 'N/A'}</td>
                <td>${candidate.email || 'N/A'}</td>
                <td>${candidate.phone || 'N/A'}</td>
                <td>${candidate.positionApplied || 'N/A'}</td>
                <td>${Array.isArray(candidate.skills) ? candidate.skills.join(', ') : (candidate.skills || 'N/A')}</td>
                <td>${candidate.experience ? candidate.experience + ' years' : 'N/A'}</td>
                <td>${candidate.referrerName ? 'Referred' : 'Direct'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ` : '<p>No applications found.</p>'}
        
        <!-- Jobs Table -->
        <h2>Active & Closing Soon Jobs Report</h2>
        ${jobs.filter(job => {
          const status = getJobStatusForExport(job);
          return status === 'Open' || status === 'Closing Soon';
        }).length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Job Title</th>
              <th>Department</th>
              <th>Location</th>
              <th>Employment Type</th>
              <th>Status</th>
              <th>Closing Date</th>
            </tr>
          </thead>
          <tbody>
            ${jobs.filter(job => {
              const status = getJobStatusForExport(job);
              return status === 'Open' || status === 'Closing Soon';
            }).map(job => {
              const status = getJobStatusForExport(job);
              return `
              <tr>
                <td>${job.jobId || job.id || 'N/A'}</td>
                <td>${job.jobTitle || job.job_title || job.title || 'N/A'}</td>
                <td>${job.department || 'N/A'}</td>
                <td>${job.location || 'N/A'}</td>
                <td>${job.employmentType || job.employment_type || job.type || 'Full-time'}</td>
                <td>${status}</td>
                <td>${job.jobClosedDate ? new Date(job.jobClosedDate).toLocaleDateString() : 'N/A'}</td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
        ` : '<p>No active or closing soon jobs found.</p>'}
        
        <!-- Interviews Table -->
        <h2>Scheduled Interviews Report</h2>
        ${interviews.filter(i => i.status?.toLowerCase() === 'scheduled').length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>Position</th>
              <th>Interview Date</th>
              <th>Round</th>
              <th>Interviewer</th>
              <th>Status</th>
              <th>Result</th>
              <th>Candidate Name</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            ${interviews.filter(i => i.status?.toLowerCase() === 'scheduled').map(interview => `
              <tr>
                <td>${interview.positionApplied || 'N/A'}</td>
                <td>${interview.interviewDate ? new Date(interview.interviewDate).toLocaleDateString() : 'N/A'}</td>
                <td>${interview.round || 'N/A'}</td>
                <td>${interview.interviewer || 'N/A'}</td>
                <td>${getInterviewStatusForExport(interview)}</td>
                <td>${interview.result || 'N/A'}</td>
                <td>${interview.name || 'N/A'}</td>
                <td>${interview.candidateEmail || 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ` : '<p>No scheduled interviews found.</p>'}
        
        <!-- Offers Table -->
        <h2>Pending Offers Report</h2>
        ${allOffers.filter(offer => offer.status?.toLowerCase() === 'pending').length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>Job Title</th>
              <th>Fixed Salary</th>
              <th>Variable Salary</th>
              <th>Joining Bonus</th>
              <th>Offer Date</th>
              <th>Joining Date</th>
              <th>Status</th>
              <th>Candidate Name</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            ${allOffers.filter(offer => offer.status?.toLowerCase() === 'pending').map(offer => `
              <tr>
                <td>${offer.jobTitle || offer.job_title || 'N/A'}</td>
                <td>${offer.salaryFixed || offer.salary_fixed || offer.fixedSalary || 'N/A'}</td>
                <td>${offer.salaryVariable || offer.salary_variable || offer.variableSalary || 'N/A'}</td>
                <td>${offer.joiningBonus || offer.joining_bonus || offer.bonus || 'N/A'}</td>
                <td>${offer.offerDate || offer.offer_date ? new Date(offer.offerDate || offer.offer_date).toLocaleDateString() : 'N/A'}</td>
                <td>${offer.joiningDate || offer.joining_date ? new Date(offer.joiningDate || offer.joining_date).toLocaleDateString() : 'N/A'}</td>
                <td>${offer.status ? offer.status.charAt(0).toUpperCase() + offer.status.slice(1) : 'Pending'}</td>
                <td>${offer.candidateName || 'N/A'}</td>
                <td>${offer.candidateEmail || 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ` : '<p>No pending offers found.</p>'}
        
        <!-- Onboarding Table -->
        <h2>Document Verification Report</h2>
        ${onboardings.filter(onboarding => {
          const processedOnboarding = {
            ...onboarding,
            uploadedDocuments: onboarding.documents || onboarding.uploadedDocuments || [],
            requiredDocuments: Array.isArray(onboarding.requiredDocuments) 
              ? onboarding.requiredDocuments 
              : (onboarding.requiredDocuments ? JSON.parse(onboarding.requiredDocuments) : [])
          };
          const status = calculateOnboardingStatusForExport(processedOnboarding);
          return status !== 'Completed';
        }).length > 0 ? `
         <table>
          <thead>
            <tr>
              <th>Candidate Name</th>
              <th>Employee ID</th>
              <th>Email</th>
              <th>Process</th>
              <th>Status</th>
              <th>Completion Date</th>
              <th>Required Documents</th>
              <th>Uploaded Documents</th>
            </tr>
          </thead>
          <tbody>
            ${onboardings.map(onboarding => {
              const processedOnboarding = {
                ...onboarding,
                uploadedDocuments: onboarding.documents || onboarding.uploadedDocuments || [],
                requiredDocuments: Array.isArray(onboarding.requiredDocuments) 
                  ? onboarding.requiredDocuments 
                  : (onboarding.requiredDocuments ? JSON.parse(onboarding.requiredDocuments) : [])
              };
              const status = calculateOnboardingStatusForExport(processedOnboarding);
              if (status === 'Completed') return '';
              
              const uploadedDocsInfo = (() => {
                const docs = processedOnboarding.uploadedDocuments;
                if (!docs || docs.length === 0) return 'No documents';
                return `${docs.length} documents`;
              })();
              
              const requiredDocsInfo = (() => {
                const docs = processedOnboarding.requiredDocuments;
                if (!docs || docs.length === 0) return 'N/A';
                return `${docs.length} documents`;
              })();
              
              return `
                <tr>
                  <td>${onboarding.candidateName || 'N/A'}</td>
                  <td>${onboarding.employee_id || 'N/A'}</td>
                  <td>${onboarding.candidateEmail || 'N/A'}</td>
                  <td>${onboarding.onboardingProcess?.replace('-', ' ') || 'N/A'}</td>
                  <td>${status}</td>
                  <td>${onboarding.taskCompletionDate ? new Date(onboarding.taskCompletionDate).toLocaleDateString() : 'N/A'}</td>
                  <td>${requiredDocsInfo}</td>
                  <td>${uploadedDocsInfo}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        ` : '<p>No pending document verification processes found.</p>'}
        
        <div class="footer">
          <p>This report was generated from the Recruitment Management System.</p>
          <p>Period: ${periodText} Report | Generated on: ${currentDate}</p>
        </div>
      </body>
      </html>
    `;
  };
  
  // PDF export function using html2pdf - generates PDF directly without print dialog
  const handleExportPDF = async () => {
    try {
      setExportLoading({ ...exportLoading, pdf: true });
      
      const htmlContent = generateExportHTML({ includeSummary: false });
      
      // Create a temporary div to hold the content
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      element.style.padding = '20px';
      element.style.backgroundColor = 'white';
      document.body.appendChild(element);
      
      // Configure PDF options
      const yearSuffix = dateRange === 'yearly' ? `_${selectedYear}` : '';
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `Recruitment_Report_${dateRange}${yearSuffix}_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, letterRendering: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      };
      
      // Generate PDF
      await html2pdf().set(opt).from(element).save();
      
      // Remove the temporary element
      document.body.removeChild(element);
      
      setTimeout(() => {
        setExportLoading({ ...exportLoading, pdf: false });
      }, 2000);
    } catch (error) {
      //console.error('Error exporting PDF:', error);
      setExportLoading({ ...exportLoading, pdf: false });
      setError('Failed to export PDF. Please try again.');
    }
  };
  
  // DOC export function - downloads as .doc file
  const handleExportDOC = async () => {
    try {
      setExportLoading({ ...exportLoading, doc: true });
      
      const htmlContent = generateExportHTML({ includeSummary: false });
      
      // Create a blob with .doc extension
      const blob = new Blob([htmlContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const currentDate = new Date().toISOString().slice(0, 10);
      const periodText = dateRange === 'monthly' ? 'Monthly' : dateRange === 'quarterly' ? 'Quarterly' : 'Yearly';
      
      link.href = url;
      link.download = `Recruitment_Report_${periodText}_${currentDate}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setTimeout(() => {
        setExportLoading({ ...exportLoading, doc: false });
      }, 2000);
    } catch (error) {
      //console.error('Error exporting DOC:', error);
      setExportLoading({ ...exportLoading, doc: false });
      setError('Failed to export DOC. Please try again.');
    }
  };

  // Enhanced Summary Cards Component (5 cards)
  const SummaryCards = () => {
    const interviewStats = getInterviewStats();
    const offerStats = getOfferStats();
    const onboardingStats = getOnboardingStats();
    
    const summaryData = [
      { 
        title: 'Total Applications', 
        value: candidates.length, 
        icon: Person, 
        color: '#1976d2',
        onClick: () => openModal('candidates', candidates, 'Total Applications Details')
      },
      { 
        title: 'Active Jobs', 
        value: getJobStats().activeJobs, 
        icon: Work, 
        color: '#388e3c',
        onClick: () => openModal('jobs', jobs.filter(j => (j.status || 'Open').toLowerCase() === 'open' || (j.status || '').toLowerCase() === 'active'), 'Active Jobs Details')
      },
      { 
        title: 'Interviews Scheduled', 
        value: totalInterviews,
        icon: Schedule, 
        color: '#f57c00',
        onClick: () => openModal('interviews', interviews, 'Scheduled Interviews Details')
      },
      { 
        title: 'Offers Sent', 
        value: allOffers.length,
        icon: Assignment, 
        color: '#7b1fa2',
        onClick: () => openModal('offers', allOffers, 'Offers Sent Details')
      },
      { 
        title: 'Onboarding Process', 
        value: onboardingStats.total,
        icon: Groups, 
        color: '#d32f2f',
        onClick: () => openModal('onboardings', onboardings, 'Onboarding Process Details')
      }
    ];

    return (
      <Grid container spacing={3} mb={4}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card
              onClick={item.onClick}
              sx={{ 
                cursor: 'pointer', 
                transition: 'all 0.2s ease-in-out', 
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 } 
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color={item.color}>
                      {loading ? <CircularProgress size={24} /> : item.value}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.title}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: item.color, width: 30, height: 30 }}>
                    <item.icon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const PredictionCards = () => {
    const referralPrediction = generateReferralPrediction();
    const hiringPrediction = generateHiringPrediction();
  
    // Generate trend data for referral predictions
    const generateReferralTrendData = () => {
      const currentMonth = new Date().getMonth();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      // Historical data (mock - you can replace with actual historical data)
      const historicalData = [
        { month: months[currentMonth - 3] || months[currentMonth + 9], referrals: Math.max(0, referralPrediction.current - 8), type: 'historical' },
        { month: months[currentMonth - 2] || months[currentMonth + 10], referrals: Math.max(0, referralPrediction.current - 5), type: 'historical' },
        { month: months[currentMonth - 1] || months[currentMonth + 11], referrals: Math.max(0, referralPrediction.current - 2), type: 'historical' },
        { month: months[currentMonth], referrals: referralPrediction.current, type: 'current' },
      ];
  
      // Prediction data - only current quarter
      const predictionData = [
        { month: months[(currentMonth + 1) % 12], referrals: referralPrediction.predictions.month, type: 'prediction' },
        { month: months[(currentMonth + 2) % 12], referrals: referralPrediction.predictions.month + 2, type: 'prediction' },
        { month: months[(currentMonth + 3) % 12], referrals: referralPrediction.predictions.quarter, type: 'prediction' },
      ];
  
      return [...historicalData, ...predictionData];
    };
  
    // Generate trend data for hiring predictions based on onboarding
    const generateHiringTrendData = () => {
      const currentMonth = new Date().getMonth();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      // Historical data based on actual onboarding completions
      const historicalData = [
        { month: months[currentMonth - 3] || months[currentMonth + 9], hired: Math.max(0, hiringPrediction.current - 3), type: 'historical' },
        { month: months[currentMonth - 2] || months[currentMonth + 10], hired: Math.max(0, hiringPrediction.current - 2), type: 'historical' },
        { month: months[currentMonth - 1] || months[currentMonth + 11], hired: Math.max(0, hiringPrediction.current - 1), type: 'historical' },
        { month: months[currentMonth], hired: hiringPrediction.current, type: 'current' },
      ];
  
      // Prediction data based on onboarding pipeline - only current quarter
      const predictionData = [
        { month: months[(currentMonth + 1) % 12], hired: hiringPrediction.predictions.month, type: 'prediction' },
        { month: months[(currentMonth + 2) % 12], hired: Math.round((hiringPrediction.predictions.month + hiringPrediction.predictions.quarter) / 2), type: 'prediction' },
        { month: months[(currentMonth + 3) % 12], hired: hiringPrediction.predictions.quarter, type: 'prediction' },
      ];
  
      return [...historicalData, ...predictionData];
    };
  
    const referralTrendData = generateReferralTrendData();
    const hiringTrendData = generateHiringTrendData();
  
    // Custom dot component for line chart
    const CustomDot = (props) => {
      const { cx, cy, payload } = props;
      if (payload.type === 'current') {
        return <circle cx={cx} cy={cy} r={6} fill="#ff4444" stroke="#fff" strokeWidth={2} />;
      } else if (payload.type === 'prediction') {
        return <circle cx={cx} cy={cy} r={4} fill="#1976d2" stroke="#fff" strokeWidth={2} strokeDasharray="2,2" />;
      }
      return <circle cx={cx} cy={cy} r={3} fill="#1976d2" stroke="#fff" strokeWidth={1} />;
    };
  
    return (
      <Grid container spacing={3} mb={4}>
        {/* Referral Prediction Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid', borderColor: 'grey.300', boxShadow: 2, height: '650px' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <TrendingUp sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6">Future Job Posting Predictions</Typography>
              </Box>
              
              {/* Current Stats First */}
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                    <Typography variant="h4" color="#1976d2" fontWeight="bold">
                      {referralPrediction.current}
                    </Typography>
                    <Typography variant="caption">Current Job Postings</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                    <Typography variant="h4" color="#1976d2" fontWeight="bold">
                      {referralPrediction.rate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption">Posting Rate</Typography>
                  </Box>
                </Grid>
              </Grid>
  
              {/* Predictions - Only Current Quarter */}
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>Predictions:</strong>
                </Typography>
                <Grid container spacing={1} justifyContent="center">
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Chip label={`${referralPrediction.predictions.month}`} size="small" color="primary" />
                      <Typography variant="caption" display="block">Next Month</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Chip label={`${referralPrediction.predictions.quarter}`} size="small" color="primary" />
                      <Typography variant="caption" display="block"> Quarter</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
  
              {/* Chart at Bottom */}
              <Box sx={{ height: '300px', flex: 1 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>Trend Analysis:</strong>
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={referralTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip 
                      formatter={(value, name) => [value, 'Job Postings']}
                      labelFormatter={(label) => `Month: ${label}`}
                      contentStyle={{ 
                        backgroundColor: '#f5f5f5', 
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="referrals" 
                      stroke="#1976d2" 
                      strokeWidth={2}
                      dot={<CustomDot />}
                      strokeDasharray={(entry) => entry?.type === 'prediction' ? '5 5' : '0'}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Hiring Prediction Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid', borderColor: 'grey.300', boxShadow: 2, height: '650px' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Psychology sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="h6">Hiring Pattern Predictions</Typography>
              </Box>
              
              {/* Calculate hiring prediction using proper status */}
              {(() => {
                // Use the same calculateOnboardingStatus function
                const calculateOnboardingStatus = (request) => {
                  const requiredDocs = request.requiredDocuments || [];
                  const uploadedDocs = request.uploadedDocuments || [];
                  
                  if (requiredDocs.length === 0) {
                    return { status: "No Requirements", color: "default" };
                  }
                  
                  if (uploadedDocs.length === 0) {
                    return { status: "Pending", color: "error" };
                  }
                  
                  const uploadedDocTypes = uploadedDocs.map(doc => doc.documentType).filter(Boolean);
                  const missingDocs = requiredDocs.filter(reqDoc => !uploadedDocTypes.includes(reqDoc));
                  
                  if (missingDocs.length === 0) {
                    const allApproved = uploadedDocs.every(doc => doc.status === 'Approved');
                    const hasRejected = uploadedDocs.some(doc => doc.status === 'Rejected');
                    
                    if (hasRejected) {
                      return { status: "Action Required", color: "warning" };
                    } else if (allApproved) {
                      return { status: "Completed", color: "success" };
                    } else {
                      return { status: "Under Review", color: "info" };
                    }
                  } else {
                    return { status: "In Progress", color: "primary" };
                  }
                };
  
                // Calculate proper hiring statistics
                const processedOnboardings = onboardings.map(onboarding => ({
                  ...onboarding,
                  uploadedDocuments: onboarding.documents || onboarding.uploadedDocuments || [],
                  requiredDocuments: Array.isArray(onboarding.requiredDocuments) 
                    ? onboarding.requiredDocuments 
                    : (onboarding.requiredDocuments ? JSON.parse(onboarding.requiredDocuments) : [])
                }));
  
                const statusCounts = processedOnboardings.reduce((acc, onboarding) => {
                  const statusInfo = calculateOnboardingStatus(onboarding);
                  const status = statusInfo.status.toLowerCase();
                  
                  acc[status] = (acc[status] || 0) + 1;
                  return acc;
                }, {});
  
                const completed = statusCounts['completed'] || 0;
                const inProgress = statusCounts['in progress'] || 0;
                const pending = statusCounts['pending'] || 0;
                const underReview = statusCounts['under review'] || 0;
                const actionRequired = statusCounts['action required'] || 0;
                const total = processedOnboardings.length;
                
                const successRate = total > 0 ? (completed / total) * 100 : 0;
                
                // Calculate predictions based on current pipeline
                const pipelineTotal = inProgress + pending + underReview;
                const estimatedSuccessRate = successRate > 0 ? successRate / 100 : 0.7;
                
                const predictedMonthlyHires = Math.round(pipelineTotal * estimatedSuccessRate * 0.3);
                const predictedQuarterlyHires = Math.round(pipelineTotal * estimatedSuccessRate * 0.8);
  
                const hiringPredictionCalc = {
                  current: completed,
                  rate: successRate,
                  inProgressOnboarding: inProgress,
                  pendingOnboarding: pending,
                  underReviewOnboarding: underReview,
                  actionRequiredOnboarding: actionRequired,
                  predictions: {
                    month: Math.max(predictedMonthlyHires, 1),
                    quarter: Math.max(predictedQuarterlyHires, 3)
                  }
                };
  
                return (
                  <>
                    {/* Current Stats First */}
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={6}>
                        <Box textAlign="center" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          <Typography variant="h4" color="#4caf50" fontWeight="bold">
                            {hiringPredictionCalc.current}
                          </Typography>
                          <Typography variant="caption">Successfully Onboarded</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box textAlign="center" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          <Typography variant="h4" color="#4caf50" fontWeight="bold">
                            {hiringPredictionCalc.rate.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption">Success Rate</Typography>
                        </Box>
                      </Grid>
                    </Grid>
  
                    {/* Pipeline Info */}
                    <Box mb={2} sx={{ bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Current Pipeline:</strong>
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">In Progress:</Typography>
                            <Typography variant="caption" fontWeight="bold">{hiringPredictionCalc.inProgressOnboarding}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">Under Review:</Typography>
                            <Typography variant="caption" fontWeight="bold">{hiringPredictionCalc.underReviewOnboarding}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">Pending:</Typography>
                            <Typography variant="caption" fontWeight="bold">{hiringPredictionCalc.pendingOnboarding}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">Action Required:</Typography>
                            <Typography variant="caption" fontWeight="bold" color="warning.main">
                              {hiringPredictionCalc.actionRequiredOnboarding}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
  
                    {/* Predictions - Only Current Quarter */}
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Predictions:</strong>
                      </Typography>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item xs={6}>
                          <Box textAlign="center">
                            <Chip label={`${hiringPredictionCalc.predictions.month}`} size="small" color="success" />
                            <Typography variant="caption" display="block">Next Month</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box textAlign="center">
                            <Chip label={`${hiringPredictionCalc.predictions.quarter}`} size="small" color="success" />
                            <Typography variant="caption" display="block"> Quarter</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
  
                    {/* Chart at Bottom */}
                    <Box sx={{ height: '300px', flex: 1 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Hiring Trend:</strong>
                      </Typography>
                      <ResponsiveContainer width="100%" height="90%">
                        <AreaChart data={hiringTrendData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="month" 
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <RechartsTooltip 
                            formatter={(value, name) => [value, 'Hired']}
                            labelFormatter={(label) => `Month: ${label}`}
                            contentStyle={{ 
                              backgroundColor: '#f5f5f5', 
                              border: '1px solid #ccc',
                              borderRadius: '4px'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="hired" 
                            stroke="#4caf50" 
                            fill="#4caf50" 
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  // Applications Report Component
  const ApplicationsReport = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(4); // Fixed at 4 rows per page as requested
  
    const totalReferred = candidates.filter(candidate => candidate.referrerName).length;
    const directApplied = candidates.length - totalReferred;
    
    // Prepare chart data
    const chartData = [
      { name: 'Direct ', value: directApplied, color: '#0088FE' },
      { name: 'Referred ', value: totalReferred, color: '#00C49F' }
    ];
  
    // Pagination handlers
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };
  
    // Get paginated candidates
    const paginatedCandidates = candidates.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  
    return (
      <Box sx={{}}>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={7}>
            <Card sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              boxShadow: 2
            }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Applications Overview</Typography>
                </Box>
                
                {loading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Candidate</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Position Applied</TableCell>
                            <TableCell>Skills</TableCell>
                            <TableCell>Experience</TableCell>
                            <TableCell>Application Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {candidates.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography color="textSecondary">No applications found</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedCandidates.map((candidate, index) => (
                              <TableRow key={candidate.id || index}>
                                <TableCell>
                                  <Box display="flex" alignItems="center">
                                    <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                      {(candidate.name || 'N').charAt(0).toUpperCase()}
                                    </Avatar>
                                    {candidate.name || 'N/A'}
                                  </Box>
                                </TableCell>
                                <TableCell>{candidate.email || 'N/A'}</TableCell>
                                <TableCell>{candidate.phone || 'N/A'}</TableCell>
                                <TableCell>{candidate.positionApplied || 'N/A'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {candidate.skills?.join(', ') || 'N/A'}
                                  </Typography>
                                </TableCell>
<TableCell>{candidate.experience ? `${candidate.experience} years` : 'N/A'}</TableCell>
                                <TableCell>
                                  {candidate.referrerName ? (
                                    <Chip 
                                      label="Referred" 
                                      color="success" 
                                      size="small"
                                      icon={<PersonAdd />}
                                    />
                                  ) : (
                                    <Chip 
                                      label="Direct" 
                                      color="primary" 
                                      size="small"
                                      icon={<Person />}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination Component */}
                    {candidates.length > 0 && (
                      <TablePagination
                        rowsPerPageOptions={[]} // Hide rows per page options since it's fixed at 4
                        component="div"
                        count={candidates.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={({ from, to, count }) => 
                          `${from}-${to} of ${count}`
                        }
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Card sx={{ 
              height: '100%',
              border: '1px solid',
              borderColor: 'grey.300',
              boxShadow: 2  
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Application Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box 
                    onClick={() => openModal('candidates', candidates, 'Total Applications Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Total Applications:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {candidates.length}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('candidates', candidates.filter(c => !c.referrerName), 'Direct Applications Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Direct Applications:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#0088FE">
                      {directApplied}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('candidates', candidates.filter(c => c.referrerName), 'Referred Applications Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Referred Applications:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#00C49F">
                      {totalReferred}
                    </Typography>
                  </Box>
                  
                  {candidates.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {chartData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.color}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (entry.name.includes('Direct')) {
                                    openModal('candidates', candidates.filter(c => !c.referrerName), 'Direct Applications Details');
                                  } else {
                                    openModal('candidates', candidates.filter(c => c.referrerName), 'Referred Applications Details');
                                  }
                                }}
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Enhanced Jobs Report Component
  const JobsReport = () => {
    const jobStats = getJobStats();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    
    // Helper function to check if a job is closing soon
    const isClosingSoon = (job) => {
        if (!job.jobClosedDate) return false;
        const closingDate = new Date(job.jobClosedDate);
        const now = new Date();
        const daysUntilClosing = Math.ceil((closingDate - now) / (1000 * 60 * 60 * 24));
        return daysUntilClosing <= 7 && daysUntilClosing > 0; // Assuming 7 days is "closing soon"
    };
    
    // Filter to show active jobs and closing soon jobs
    const activeAndClosingSoonJobs = jobs.filter(job => {
        const status = job.status || 'Open';
        const isActive = status.toLowerCase() === 'open' || status.toLowerCase() === 'active';
        const isClosingSoonJob = isClosingSoon(job);
        
        return isActive || isClosingSoonJob;
    });
    
    // Pagination logic
    const paginatedJobs = activeAndClosingSoonJobs.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
    
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{border: '1px solid',
        borderColor: 'grey.300',
        boxShadow: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Active & Closing Soon Job Postings ({activeAndClosingSoonJobs.length} jobs)
                  </Typography>
                </Box>
                
                {loading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Job ID</TableCell>
                            <TableCell>Job Title</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Employment Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Closing Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeAndClosingSoonJobs.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography color="textSecondary">No active or closing soon job postings found</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedJobs.map((job, index) => (
                              <TableRow key={job.id || job.jobId || index}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {job.jobId || job.id || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2">
                                    {job.jobTitle || job.job_title || job.title || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{job.department || 'N/A'}</TableCell>
                                <TableCell>{job.location || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={job.employmentType || job.employment_type || job.type || 'Full-time'} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={isClosingSoon(job) ? 'Closing Soon' : (job.status || 'Open')} 
                                    color={isClosingSoon(job) ? 'warning' : getStatusColor(job.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {job.jobClosedDate ? new Date(job.jobClosedDate).toLocaleDateString() : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination Component */}
                    {activeAndClosingSoonJobs.length > 0 && (
                      <TablePagination
                        component="div"
                        count={activeAndClosingSoonJobs.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%',border: '1px solid',
        borderColor: 'grey.300',
        boxShadow: 2  }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Job Status Overview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box 
                    onClick={() => openModal('jobs', jobs.filter(j => (j.status || 'Open').toLowerCase() === 'open' || (j.status || '').toLowerCase() === 'active'), 'Active Jobs Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: '#4caf50', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Active Jobs:</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#4caf50">
                      {jobStats.activeJobs}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('jobs', jobs.filter(j => isClosingSoon(j)), 'Closing Soon Jobs Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <HourglassEmpty sx={{ color: '#ff9800', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Closing Soon:</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#ff9800">
                      {jobStats.closingSoonJobs}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('jobs', jobs.filter(j => (j.status || '').toLowerCase() === 'closed'), 'Closed Jobs Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Cancel sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Closed Jobs:</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#757575">
                      {jobStats.closedJobs}
                    </Typography>
                  </Box>
                  
                  {jobs.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={[
                          { name: 'Active', value: jobStats.activeJobs, fill: '#4caf50' },
                          { name: 'Closing Soon', value: jobStats.closingSoonJobs, fill: '#ff9800' },
                          { name: 'Closed', value: jobStats.closedJobs, fill: '#757575' }
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="value">
                            {[
                              { fill: '#4caf50', onClick: () => openModal('jobs', jobs.filter(j => (j.status || 'Open').toLowerCase() === 'open' || (j.status || '').toLowerCase() === 'active'), 'Active Jobs Details') },
                              { fill: '#ff9800', onClick: () => openModal('jobs', jobs.filter(j => isClosingSoon(j)), 'Closing Soon Jobs Details') },
                              { fill: '#757575', onClick: () => openModal('jobs', jobs.filter(j => (j.status || '').toLowerCase() === 'closed'), 'Closed Jobs Details') }
                            ].map((entry, index) => (
                              <Cell key={`bar-cell-${index}`} fill={entry.fill} onClick={entry.onClick} style={{ cursor: 'pointer' }} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  // Fixed Interview Report Component
  const InterviewReport = () => {
    const interviewStats = getInterviewStats();
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(5);
    
    // Filter interviews to show only scheduled status
    const scheduledInterviews = interviews.filter(interview => 
        interview.status?.toLowerCase() === 'scheduled'
    );
    
    // Calculate pagination
    const paginatedInterviews = scheduledInterviews.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
    
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7.8}>
            <Card sx={{border: '1px solid',
        borderColor: 'grey.300',
        boxShadow: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Scheduled Interviews ({scheduledInterviews.length} interviews)
                  </Typography>
                </Box>
                
                {loading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Position</TableCell>
                            <TableCell>Interview Date</TableCell>
                            <TableCell>Round</TableCell>
                            <TableCell>Interviewer</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Result</TableCell>
                            <TableCell>Candidate Name</TableCell>
                            <TableCell>Email</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scheduledInterviews.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                <Typography color="textSecondary">No scheduled interviews found</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedInterviews.map((interview, index) => (
                              <TableRow key={interview.id || index}>
                                <TableCell>{interview.positionApplied || 'N/A'}</TableCell>
                                <TableCell>
                                  {interview.interviewDate ? 
                                    new Date(interview.interviewDate).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={interview.round || 'N/A'} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{interview.interviewer || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={interview.status || 'Scheduled'} 
                                    color={getStatusColor(interview.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {interview.result && (
                                    <Chip 
                                      label={interview.result} 
                                      color={getStatusColor(interview.result)}
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center">
                                    {resolveCandidateName(interview)}
                                  </Box>
                                </TableCell>
                                <TableCell>{resolveCandidateEmail(interview)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {scheduledInterviews.length > 0 && (
                      <TablePagination
                        component="div"
                        count={scheduledInterviews.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[5]}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4.2}>
            <Card sx={{ height: '100%' ,border: '1px solid',
        borderColor: 'grey.300',
        boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Interview Statistics
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box 
                    onClick={() => openModal('interviews', interviews.filter(i => i.status?.toLowerCase() === 'scheduled' || i.status?.toLowerCase() === 'pending'), 'Scheduled Interviews Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Total Scheduled:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#2196f3">
                      {totalInterviews}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('interviews', interviews.filter(i => i.status?.toLowerCase() === 'completed'), 'Completed Interviews Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Completed:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#4caf50">
                      {interviewStats.completed}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('interviews', interviews.filter(i => i.result?.toLowerCase() === 'selected' || i.status?.toLowerCase() === 'selected'), 'Selected Candidates Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Selected:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#8bc34a">
                      {interviewStats.selected}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('interviews', interviews.filter(i => i.result?.toLowerCase() === 'rejected' || i.status?.toLowerCase() === 'rejected'), 'Rejected Candidates Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Rejected:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#f44336">
                      {interviewStats.rejected}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('interviews', interviews.filter(i => i.status?.toLowerCase() === 'cancelled'), 'Cancelled Interviews Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Cancelled:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#ff9800">
                      {interviewStats.cancelled}
                    </Typography>
                  </Box>
                  
                  {interviews.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Scheduled', value: interviewStats.scheduled, fill: '#2196f3', data: interviews.filter(i => i.status?.toLowerCase() === 'scheduled' || i.status?.toLowerCase() === 'pending') },
                              { name: 'Completed', value: interviewStats.completed, fill: '#4caf50', data: interviews.filter(i => i.status?.toLowerCase() === 'completed') },
                              { name: 'Selected', value: interviewStats.selected, fill: '#8bc34a', data: interviews.filter(i => i.result?.toLowerCase() === 'selected' || i.status?.toLowerCase() === 'selected') },
                              { name: 'Rejected', value: interviewStats.rejected, fill: '#f44336', data: interviews.filter(i => i.result?.toLowerCase() === 'rejected' || i.status?.toLowerCase() === 'rejected') },
                              { name: 'Cancelled', value: interviewStats.cancelled, fill: '#ff9800', data: interviews.filter(i => i.status?.toLowerCase() === 'cancelled') }
                            ].filter(item => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: 'Scheduled', value: interviewStats.scheduled, fill: '#2196f3', data: interviews.filter(i => i.status?.toLowerCase() === 'scheduled' || i.status?.toLowerCase() === 'pending') },
                              { name: 'Completed', value: interviewStats.completed, fill: '#4caf50', data: interviews.filter(i => i.status?.toLowerCase() === 'completed') },
                              { name: 'Selected', value: interviewStats.selected, fill: '#8bc34a', data: interviews.filter(i => i.result?.toLowerCase() === 'selected' || i.status?.toLowerCase() === 'selected') },
                              { name: 'Rejected', value: interviewStats.rejected, fill: '#f44336', data: interviews.filter(i => i.result?.toLowerCase() === 'rejected' || i.status?.toLowerCase() === 'rejected') },
                              { name: 'Cancelled', value: interviewStats.cancelled, fill: '#ff9800', data: interviews.filter(i => i.status?.toLowerCase() === 'cancelled') }
                            ].filter(item => item.value > 0).map((entry, index) => (
                              <Cell 
                                key={`interview-cell-${index}`} 
                                fill={entry.fill} 
                                style={{ cursor: 'pointer' }}
                                onClick={() => openModal('interviews', entry.data, `${entry.name} Interviews Details`)}
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const OffersReport = () => {
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(5); // Fixed at 5 rows per page
  
    // Filter offers to show only pending status
    const pendingOffers = allOffers.filter(offer => offer.status?.toLowerCase() === 'pending');
  
    const getOfferStats = () => {
      const pendingOffers = allOffers.filter(offer => offer.status?.toLowerCase() === 'pending').length;
      const approvedOffers = allOffers.filter(offer => offer.status?.toLowerCase() === 'approved').length;
      const rejectedOffers = allOffers.filter(offer => offer.status?.toLowerCase() === 'rejected').length;
      
      return { pendingOffers, approvedOffers, rejectedOffers };
    };
  
    const offerStats = getOfferStats();
    
    // Function to capitalize status
    const capitalizeStatus = (status) => {
      if (!status) return 'Pending';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    };
  
    // Function to get candidate name - enhanced version
    const getCandidateName = (offer) => {
      const name = offer.candidateName || 
                   offer.candidate_name || 
                   offer.name || 
                   offer.candidateDetails?.name ||
                   offer.candidate?.name;
      
      // Don't show "Candidate Information Pending" in the table, show "Pending" instead
      if (name === 'Candidate Information Pending') {
        return 'Information Pending';
      }
      
      return name || 'N/A';
    };
  
    // Function to get candidate email - try multiple possible field names
    const getCandidateEmail = (offer) => {
      return offer.candidateEmail || 
             offer.candidate_email || 
             offer.email || 
             offer.candidateDetails?.email ||
             offer.candidate?.email ||
             'N/A';
    };
  
    // Pagination handlers
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };
  
    // Calculate paginated data from pending offers only
    const paginatedOffers = pendingOffers.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7.5}>
            <Card sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              boxShadow: 2 
            }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Pending Offers Report ({pendingOffers.length} offers)</Typography>
                </Box>
                
                {loading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Job Title</TableCell>
                            <TableCell>Fixed Salary</TableCell>
                            <TableCell>Variable Salary</TableCell>
                            <TableCell>Joining Bonus</TableCell>
                            <TableCell>Offer Date</TableCell>
                            <TableCell>Joining Date</TableCell> 
                            <TableCell>Status</TableCell>
                            <TableCell>Candidate Name</TableCell>
                            <TableCell>Email</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingOffers.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center">
                                <Typography color="textSecondary">No pending offers found</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedOffers.map((offer, index) => (
                              <TableRow key={offer.id || index}>
                                <TableCell>{offer.jobTitle || offer.job_title || 'N/A'}</TableCell>
                                <TableCell>
                                  {offer.salaryFixed || offer.salary_fixed || offer.fixedSalary || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {offer.salaryVariable || offer.salary_variable || offer.variableSalary || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {offer.joiningBonus || offer.joining_bonus || offer.bonus || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {offer.offerDate || offer.offer_date ? 
                                    new Date(offer.offerDate || offer.offer_date).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {offer.joiningDate || offer.joining_date ? 
                                    new Date(offer.joiningDate || offer.joining_date).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={capitalizeStatus(offer.status)} 
                                    color={getStatusColor(offer.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{resolveCandidateName(offer)}</TableCell>
                                <TableCell>{resolveCandidateEmail(offer)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination Component */}
                    {pendingOffers.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <TablePagination
                          component="div"
                          count={pendingOffers.length}
                          page={page}
                          onPageChange={handleChangePage}
                          rowsPerPage={rowsPerPage}
                          rowsPerPageOptions={[5]} // Only show 5 as option since it's fixed
                          showFirstButton
                          showLastButton
                          labelDisplayedRows={
                            ({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                          }
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4.5}>
            <Card sx={{ 
              height: '100%',
              border: '1px solid',
              borderColor: 'grey.300',
              boxShadow: 2  
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Offer Status Overview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box 
                    onClick={() => openModal('offers', allOffers, 'Total Offers Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Total Offers:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {allOffers.length}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('offers', allOffers.filter(o => o.status?.toLowerCase() === 'pending'), 'Pending Offers Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Pending Offers:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#FFBB28">
                      {offerStats.pendingOffers}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('offers', allOffers.filter(o => o.status?.toLowerCase() === 'approved' || o.status?.toLowerCase() === 'accepted'), 'Approved Offers Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Approved Offers:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#00C49F">
                      {offerStats.approvedOffers}
                    </Typography>
                  </Box>
                  <Box 
                    onClick={() => openModal('offers', allOffers.filter(o => o.status?.toLowerCase() === 'rejected'), 'Rejected Offers Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Rejected Offers:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#FF4444">
                      {offerStats.rejectedOffers}
                    </Typography>
                  </Box>
                  
                  {allOffers.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Pending', value: offerStats.pendingOffers, fill: '#FFBB28', data: allOffers.filter(o => o.status?.toLowerCase() === 'pending') },
                              { name: 'Approved', value: offerStats.approvedOffers, fill: '#00C49F', data: allOffers.filter(o => o.status?.toLowerCase() === 'approved' || o.status?.toLowerCase() === 'accepted') },
                              { name: 'Rejected', value: offerStats.rejectedOffers, fill: '#FF4444', data: allOffers.filter(o => o.status?.toLowerCase() === 'rejected') }
                            ].filter(item => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: 'Pending', value: offerStats.pendingOffers, fill: '#FFBB28', data: allOffers.filter(o => o.status?.toLowerCase() === 'pending') },
                              { name: 'Approved', value: offerStats.approvedOffers, fill: '#00C49F', data: allOffers.filter(o => o.status?.toLowerCase() === 'approved' || o.status?.toLowerCase() === 'accepted') },
                              { name: 'Rejected', value: offerStats.rejectedOffers, fill: '#FF4444', data: allOffers.filter(o => o.status?.toLowerCase() === 'rejected') }
                            ].filter(item => item.value > 0).map((entry, index) => (
                              <Cell 
                                key={`offer-cell-${index}`} 
                                fill={entry.fill} 
                                style={{ cursor: 'pointer' }}
                                onClick={() => openModal('offers', entry.data, `${entry.name} Offers Details`)} 
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // FIXED: Onboarding Report Component with proper document status display
  const OnboardingReport = () => {
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Add the same status calculation function from OnboardingListPage
    const calculateOnboardingStatus = (request) => {
      const requiredDocs = request.requiredDocuments || [];
      const uploadedDocs = request.uploadedDocuments || [];
      
      if (requiredDocs.length === 0) {
        return { status: "No Requirements", color: "default" };
      }
      
      if (uploadedDocs.length === 0) {
        return { status: "Pending", color: "error" };
      }
      
      // Check if all required documents are uploaded
      const uploadedDocTypes = uploadedDocs.map(doc => doc.documentType).filter(Boolean);
      const missingDocs = requiredDocs.filter(reqDoc => !uploadedDocTypes.includes(reqDoc));
      
      if (missingDocs.length === 0) {
        // All documents uploaded, check if all are approved
        const allApproved = uploadedDocs.every(doc => doc.status === 'Approved');
        const hasRejected = uploadedDocs.some(doc => doc.status === 'Rejected');
        
        if (hasRejected) {
          return { status: "Action Required", color: "warning" };
        } else if (allApproved) {
          return { status: "Completed", color: "success" };
        } else {
          return { status: "Under Review", color: "info" };
        }
      } else {
        // Some documents are missing
        return { status: "In Progress", color: "primary" };
      }
    };

    // Filter onboardings to show only document verification processes
    const documentVerificationOnboardings = onboardings.filter(onboarding => 
      onboarding.onboardingProcess?.toLowerCase().includes('document') || 
      onboarding.onboardingProcess?.toLowerCase().includes('verification')
    );

    // Filter out completed items from the table display
    const pendingDocumentVerifications = documentVerificationOnboardings.filter(onboarding => {
      const processedOnboarding = {
        ...onboarding,
        uploadedDocuments: onboarding.documents || onboarding.uploadedDocuments || [],
        requiredDocuments: Array.isArray(onboarding.requiredDocuments) 
          ? onboarding.requiredDocuments 
          : (onboarding.requiredDocuments ? JSON.parse(onboarding.requiredDocuments) : [])
      };
      
      const statusInfo = calculateOnboardingStatus(processedOnboarding);
      return statusInfo.status.toLowerCase() !== 'completed';
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    // Calculate paginated data
    const paginatedData = pendingDocumentVerifications.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  
    // Updated function to get onboarding stats for document verification only
    const getOnboardingStats = () => {
      const stats = {
        pending: 0,
        inProgress: 0,
        completed: 0,
        underReview: 0,
        actionRequired: 0
      };
  
      documentVerificationOnboardings.forEach(onboarding => {
        // Ensure uploadedDocuments is properly set
        const processedOnboarding = {
          ...onboarding,
          uploadedDocuments: onboarding.documents || onboarding.uploadedDocuments || [],
          requiredDocuments: Array.isArray(onboarding.requiredDocuments) 
            ? onboarding.requiredDocuments 
            : (onboarding.requiredDocuments ? JSON.parse(onboarding.requiredDocuments) : [])
        };
  
        const statusInfo = calculateOnboardingStatus(processedOnboarding);
        
        switch (statusInfo.status.toLowerCase()) {
          case 'pending':
            stats.pending++;
            break;
          case 'in progress':
            stats.inProgress++;
            break;
          case 'completed':
            stats.completed++;
            break;
          case 'under review':
            stats.underReview++;
            break;
          case 'action required':
            stats.actionRequired++;
            break;
          default:
            stats.pending++;
        }
      });
  
      return stats;
    };
  
    const onboardingStats = getOnboardingStats();
    
    // Updated chart data for document verification only (excluding No Requirements)
    const chartData = [
      { name: 'Pending', value: onboardingStats.pending, color: '#f44336' },
      { name: 'In Progress', value: onboardingStats.inProgress, color: '#2196f3' },
      { name: 'Under Review', value: onboardingStats.underReview, color: '#00bcd4' },
      { name: 'Action Required', value: onboardingStats.actionRequired, color: '#ff9800' },
      { name: 'Completed', value: onboardingStats.completed, color: '#4caf50' }
    ].filter(item => item.value > 0); // Only show statuses that have data
  
    // Helper function to safely parse required documents
    const getRequiredDocumentsDisplay = (requiredDocuments) => {
      if (!requiredDocuments) return 'N/A';
      
      try {
        // Try to parse as JSON first
        if (typeof requiredDocuments === 'string') {
          const parsed = JSON.parse(requiredDocuments);
          if (Array.isArray(parsed)) {
            return parsed.length + ' documents';
          }
        } else if (Array.isArray(requiredDocuments)) {
          return requiredDocuments.length + ' documents';
        }
        return 'N/A';
      } catch (error) {
        // If JSON parsing fails, treat as comma-separated string
        if (typeof requiredDocuments === 'string') {
          const docs = requiredDocuments.split(',').filter(doc => doc.trim() !== '');
          return docs.length + ' documents';
        }
        return 'N/A';
      }
    };
  
    // Helper function to get uploaded documents count and status
    const getUploadedDocumentsDisplay = (uploadedDocuments) => {
      if (!uploadedDocuments || !Array.isArray(uploadedDocuments) || uploadedDocuments.length === 0) {
        return { count: 0, text: 'No documents' };
      }
      
      const count = uploadedDocuments.length;
      const approved = uploadedDocuments.filter(doc => 
        doc.status?.toLowerCase() === 'approved'
      ).length;
      const rejected = uploadedDocuments.filter(doc => 
        doc.status?.toLowerCase() === 'rejected'
      ).length;
      
      if (approved === count) {
        return { count, text: `${count} documents (All approved)`, color: 'success' };
      } else if (rejected > 0) {
        return { count, text: `${count} documents (${rejected} rejected)`, color: 'error' };
      } else if (approved > 0) {
        return { count, text: `${count} documents (${approved} approved)`, color: 'info' };
      } else {
        return { count, text: `${count} documents (Pending)`, color: 'warning' };
      }
    };
  
    // Updated status color function to match the new status types
    const getStatusColor = (status) => {
      switch ((status || '').toLowerCase()) {
        case 'completed':
          return 'success';
        case 'in progress':
          return 'primary';
        case 'pending':
          return 'error';
        case 'under review':
          return 'info';
        case 'action required':
          return 'warning';
        default:
          return 'default';
      }
    };
  
    return (
      <Box>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={7}>
            <Card sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              boxShadow: 2,
              
            }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Pending Document Verification</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total: {pendingDocumentVerifications.length} records
                  </Typography>
                </Box>
                
                {loading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Candidate Name</TableCell>
                            <TableCell>Employee ID</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Process</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Completion Date</TableCell>
                            <TableCell>Required Documents</TableCell>
                            <TableCell>Uploaded Documents</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedData.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                <Typography color="textSecondary">No pending document verification processes found</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedData.map((onboarding, index) => {
                              // Process the onboarding data properly
                              const processedOnboarding = {
                                ...onboarding,
                                uploadedDocuments: onboarding.documents || onboarding.uploadedDocuments || [],
                                requiredDocuments: Array.isArray(onboarding.requiredDocuments) 
                                  ? onboarding.requiredDocuments 
                                  : (onboarding.requiredDocuments ? JSON.parse(onboarding.requiredDocuments) : [])
                              };
    
                              const statusInfo = calculateOnboardingStatus(processedOnboarding);
                              const uploadedDocsInfo = getUploadedDocumentsDisplay(processedOnboarding.uploadedDocuments);
                              
                              return (
                                <TableRow key={onboarding.id || index}>
                                  <TableCell>
                                    <Box display="flex" alignItems="center">
                                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                        {resolveCandidateName(onboarding).charAt(0).toUpperCase()}
                                      </Avatar>
                                      {resolveCandidateName(onboarding)}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{onboarding.employee_id || 'N/A'}</TableCell>
                                  <TableCell>{onboarding.candidateEmail || 'N/A'}</TableCell>
                                  <TableCell>
                                    {onboarding.onboardingProcess?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={statusInfo.status} 
                                      color={statusInfo.color}
                                      size="small"
                                      icon={
                                        statusInfo.status === 'Completed' ? <CheckCircle /> :
                                        statusInfo.status === 'In Progress' ? <HourglassEmpty /> :
                                        statusInfo.status === 'Under Review' ? <HourglassEmpty /> :
                                        statusInfo.status === 'Action Required' ? <Cancel /> : 
                                        <HourglassEmpty />
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {onboarding.taskCompletionDate ? 
                                      new Date(onboarding.taskCompletionDate).toLocaleDateString() : 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {getRequiredDocumentsDisplay(onboarding.requiredDocuments)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {uploadedDocsInfo.count > 0 ? (
                                      <Chip 
                                        label={uploadedDocsInfo.text}
                                        color={uploadedDocsInfo.color}
                                        size="small"
                                      />
                                    ) : (
                                      <Chip 
                                        label="No documents"
                                        variant="outlined"
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination Component */}
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={pendingDocumentVerifications.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Rows per page:"
                      labelDisplayedRows={({ from, to, count }) => 
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                      }
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Card sx={{ 
              height: '100%',
              border: '1px solid',
              borderColor: 'grey.300',
              boxShadow: 2  ,
             
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Document Verification Status
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box 
                    onClick={() => openModal('onboardings', documentVerificationOnboardings, 'Total Document Verifications Details')}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">Total Document Verifications:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {documentVerificationOnboardings.length}
                    </Typography>
                  </Box>
                  
                  {/* Display all status counts */}
                  {onboardingStats.pending > 0 && (
                    <Box 
                      onClick={() => openModal('onboardings', documentVerificationOnboardings.filter(o => {
                        const processed = {
                          ...o,
                          uploadedDocuments: o.documents || o.uploadedDocuments || [],
                          requiredDocuments: Array.isArray(o.requiredDocuments) ? o.requiredDocuments : (o.requiredDocuments ? JSON.parse(o.requiredDocuments) : [])
                        };
                        return calculateOnboardingStatus(processed).status.toLowerCase() === 'pending';
                      }), 'Pending Document Verification Details')}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="body2">Pending:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="#f44336">
                        {onboardingStats.pending}
                      </Typography>
                    </Box>
                  )}
                  
                  {onboardingStats.inProgress > 0 && (
                    <Box 
                      onClick={() => openModal('onboardings', documentVerificationOnboardings.filter(o => {
                        const processed = {
                          ...o,
                          uploadedDocuments: o.documents || o.uploadedDocuments || [],
                          requiredDocuments: Array.isArray(o.requiredDocuments) ? o.requiredDocuments : (o.requiredDocuments ? JSON.parse(o.requiredDocuments) : [])
                        };
                        return calculateOnboardingStatus(processed).status.toLowerCase() === 'in progress';
                      }), 'In Progress Document Verification Details')}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="body2">In Progress:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="#2196f3">
                        {onboardingStats.inProgress}
                      </Typography>
                    </Box>
                  )}
                  
                  {onboardingStats.underReview > 0 && (
                    <Box 
                      onClick={() => openModal('onboardings', documentVerificationOnboardings.filter(o => {
                        const processed = {
                          ...o,
                          uploadedDocuments: o.documents || o.uploadedDocuments || [],
                          requiredDocuments: Array.isArray(o.requiredDocuments) ? o.requiredDocuments : (o.requiredDocuments ? JSON.parse(o.requiredDocuments) : [])
                        };
                        return calculateOnboardingStatus(processed).status.toLowerCase() === 'under review';
                      }), 'Under Review Document Verification Details')}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="body2">Under Review:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="#00bcd4">
                        {onboardingStats.underReview}
                      </Typography>
                    </Box>
                  )}
                  
                  {onboardingStats.actionRequired > 0 && (
                    <Box 
                      onClick={() => openModal('onboardings', documentVerificationOnboardings.filter(o => {
                        const processed = {
                          ...o,
                          uploadedDocuments: o.documents || o.uploadedDocuments || [],
                          requiredDocuments: Array.isArray(o.requiredDocuments) ? o.requiredDocuments : (o.requiredDocuments ? JSON.parse(o.requiredDocuments) : [])
                        };
                        return calculateOnboardingStatus(processed).status.toLowerCase() === 'action required';
                      }), 'Action Required Document Verification Details')}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="body2">Action Required:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="#ff9800">
                        {onboardingStats.actionRequired}
                      </Typography>
                    </Box>
                  )}
                  
                  {onboardingStats.completed > 0 && (
                    <Box 
                      onClick={() => openModal('onboardings', documentVerificationOnboardings.filter(o => {
                        const processed = {
                          ...o,
                          uploadedDocuments: o.documents || o.uploadedDocuments || [],
                          requiredDocuments: Array.isArray(o.requiredDocuments) ? o.requiredDocuments : (o.requiredDocuments ? JSON.parse(o.requiredDocuments) : [])
                        };
                        return calculateOnboardingStatus(processed).status.toLowerCase() === 'completed';
                      }), 'Completed Document Verification Details')}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="body2">Completed:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="#4caf50">
                        {onboardingStats.completed}
                      </Typography>
                    </Box>
                  )}
                  
                  {documentVerificationOnboardings.length > 0 && chartData.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {chartData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.color} 
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  const filtered = documentVerificationOnboardings.filter(o => {
                                    const processed = {
                                      ...o,
                                      uploadedDocuments: o.documents || o.uploadedDocuments || [],
                                      requiredDocuments: Array.isArray(o.requiredDocuments) 
                                        ? o.requiredDocuments 
                                        : (o.requiredDocuments ? JSON.parse(o.requiredDocuments) : [])
                                    };
                                    return calculateOnboardingStatus(processed).status.toLowerCase() === entry.name.toLowerCase();
                                  });
                                  openModal('onboardings', filtered, `${entry.name} Document Verification Details`);
                                }}
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, border: '2px solid',
        borderColor: 'grey.300',
        boxShadow: 4, minHeight: '100vh', maxWidth:'1500px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Recruitment Reports
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          {/* Added Export Buttons */}
          <Tooltip title="Export to PDF">
  <Button
    variant="outlined"
    color="primary"
    startIcon={<PictureAsPdf />}
    onClick={handleExportPDF}
    disabled={exportLoading.pdf}
    sx={{
      padding: "10px 10px",
      fontSize: "0.9rem",
      textTransform: "none",
      borderRadius: "8px",
      borderWidth: 2,
      mr: 1
    }}
  >
    {exportLoading.pdf ? 'Exporting...' : 'Export to PDF'}
  </Button>
</Tooltip>

<Tooltip title="Export to DOC">
  <Button
    variant="outlined"
    color="primary"
    startIcon={<Description />}
    onClick={handleExportDOC}
    disabled={exportLoading.doc}
    sx={{
      padding: "10px 10px",
      fontSize: "0.9rem",
      textTransform: "none",
      borderRadius: "8px",
      borderWidth: 2,
      mr: 2
    }}
  >
    {exportLoading.doc ? 'Exporting...' : 'Export to DOC'}
  </Button>
</Tooltip>
          
          {/* Fixed Period & Year Filter */}
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="period-label">Period</InputLabel>
              <Select
                labelId="period-label"
                id="period-select"
                value={dateRange}
                onChange={handleDateRangeChange}
                label="Period"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>

            {dateRange === 'yearly' && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="year-label">Year</InputLabel>
                <Select
                  labelId="year-label"
                  id="year-select"
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {availableYears.map(yr => (
                    <MenuItem key={yr} value={yr}>
                      {yr} {yr === new Date().getFullYear() ? '(Current)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <SummaryCards />

           <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Predictions" />
        <Tab label="Applications" />
        <Tab label="Job Postings" />
        <Tab label="Interview Report" />
        <Tab label="Offers Report" />
        <Tab label="Onboarding" />
      </Tabs>

      
      {activeTab === 0 && <PredictionCards />}
      {activeTab === 1 && <ApplicationsReport />}
      {activeTab === 2 && <JobsReport />}
      {activeTab === 3 && <InterviewReport />}
      {activeTab === 4 && <OffersReport />}
      {activeTab === 5 && <OnboardingReport />}

      <DetailDialog />
    </Box>
  );
};

export default RecruitmentReports;